// src/components/Projects/ProjectMembersSheet.tsx
import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserPlus, Trash2 } from 'lucide-react';
import { useProjectMembers } from '@/hooks/useProjectMembers';
import { useCompanyUsers } from '@/hooks/useCompanyUsers';
import { useStableAuth } from '@/hooks/useStableAuth';
import type { ProjectMember, ProjectMemberRole } from '@/types/project';

const ROLE_LABELS: Record<ProjectMemberRole, string> = {
  owner: 'Responsável',
  editor: 'Editor',
  viewer: 'Visualizador',
};

const ROLE_DESCRIPTIONS: Record<ProjectMemberRole, string> = {
  owner: 'Gerencia tudo, incluindo membros',
  editor: 'Cria e move tarefas',
  viewer: 'Apenas visualização',
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

interface MemberRowProps {
  member: ProjectMember;
  isCurrentUser: boolean;
  isOwner: boolean;
  onUpdateRole: (memberId: string, role: ProjectMemberRole) => void;
  onRemove: (member: ProjectMember) => void;
  isUpdating: boolean;
}

function MemberRow({
  member,
  isCurrentUser,
  isOwner,
  onUpdateRole,
  onRemove,
  isUpdating,
}: MemberRowProps) {
  const name = member.full_name ?? 'Usuário';

  return (
    <div className="flex items-center gap-3 py-2.5">
      {/* Avatar */}
      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary shrink-0">
        {getInitials(name)}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {name}
          {isCurrentUser && (
            <span className="ml-1.5 text-xs text-muted-foreground font-normal">
              (você)
            </span>
          )}
        </p>
        {member.user_position && (
          <p className="text-xs text-muted-foreground truncate">
            {member.user_position}
          </p>
        )}
      </div>

      {/* Papel */}
      {isOwner && !isCurrentUser ? (
        <Select
          value={member.role}
          onValueChange={(v) =>
            onUpdateRole(member.id, v as ProjectMemberRole)
          }
          disabled={isUpdating}
        >
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(ROLE_LABELS) as ProjectMemberRole[]).map((role) => (
              <SelectItem key={role} value={role}>
                <div>
                  <p className="text-xs font-medium">{ROLE_LABELS[role]}</p>
                  <p className="text-xs text-muted-foreground">
                    {ROLE_DESCRIPTIONS[role]}
                  </p>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Badge variant={member.role === 'owner' ? 'default' : 'secondary'} className="text-xs">
          {ROLE_LABELS[member.role]}
        </Badge>
      )}

      {/* Remover */}
      {isOwner && !isCurrentUser && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(member)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

interface ProjectMembersSheetProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProjectMembersSheet({
  projectId,
  open,
  onOpenChange,
}: ProjectMembersSheetProps) {
  const { userId } = useStableAuth();
  const {
    members,
    isLoading,
    isOwner,
    addMember,
    isAddingMember,
    updateRole,
    isUpdatingRole,
    removeMember,
    isRemovingMember,
  } = useProjectMembers(projectId);

  const { users } = useCompanyUsers(members.map((m) => m.user_id));

  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<ProjectMemberRole>('editor');
  const [memberToRemove, setMemberToRemove] = useState<ProjectMember | null>(
    null
  );

  const handleAddMember = async () => {
    if (!addUserId) return;
    try {
      await addMember({ memberUserId: addUserId, role: addRole });
      setAddUserId('');
      setAddRole('editor');
    } catch {
      // erro tratado no hook
    }
  };

  const handleConfirmRemove = async () => {
    if (!memberToRemove) return;
    try {
      await removeMember(memberToRemove.id);
    } finally {
      setMemberToRemove(null);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle>Membros do projeto</SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto mt-4">
            {/* Adicionar membro — apenas owner */}
            {isOwner && (
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <p className="text-xs font-medium mb-3 text-muted-foreground uppercase tracking-wide">
                  Adicionar membro
                </p>

                <div className="flex flex-col gap-2">
                  <Select value={addUserId} onValueChange={setAddUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecionar usuário..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex gap-2">
                    <Select
                      value={addRole}
                      onValueChange={(v) => setAddRole(v as ProjectMemberRole)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(ROLE_LABELS) as ProjectMemberRole[])
                          .filter((r) => r !== 'owner')
                          .map((role) => (
                            <SelectItem key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>

                    <Button
                      size="sm"
                      onClick={handleAddMember}
                      disabled={!addUserId || isAddingMember}
                      className="shrink-0"
                    >
                      <UserPlus className="h-4 w-4 mr-1.5" />
                      {isAddingMember ? 'Adicionando...' : 'Adicionar'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Lista de membros */}
            <div>
              <p className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wide">
                {members.length} {members.length === 1 ? 'membro' : 'membros'}
              </p>

              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-12 bg-muted animate-pulse rounded"
                    />
                  ))}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {members.map((member) => (
                    <MemberRow
                      key={member.id}
                      member={member}
                      isCurrentUser={member.user_id === userId}
                      isOwner={isOwner}
                      onUpdateRole={(id, role) => updateRole({ memberId: id, role })}
                      onRemove={setMemberToRemove}
                      isUpdating={isUpdatingRole}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Confirmação de remoção */}
      <AlertDialog
        open={!!memberToRemove}
        onOpenChange={(open) => { if (!open) setMemberToRemove(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover membro</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover{' '}
              <strong>{memberToRemove?.full_name ?? 'este membro'}</strong> do
              projeto? Ele perderá acesso imediatamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemovingMember}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={isRemovingMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemovingMember ? 'Removendo...' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
